import { AuthNgSchemaTypes } from "@bfc-software/api-schemas";
import { LocalStorage } from "@bfc-software/app-toolbox";
import type { AuthService } from "@bfc-software/app-toolbox";
import { QueryClient } from "@tanstack/react-query";
import createFetchClient, { Middleware } from "openapi-fetch";
import createClient from "openapi-react-query";
import { getEnv } from "@bfc-software/bfc-web-ui/environment";
import { logger } from "@bfc-software/app-toolbox";

/**
 * The AAA operations that authenticate with the service access key (the ones
 * whose spec `security` is `accessKey` alone: the pre-login flows and the
 * org/policy lookups). Everything else -- notably `/app_user/*`, which AAA now
 * gates on the member JWT only -- gets no key. The key ships in the bundle, so
 * it is sent only where AAA still requires it. A path missing here is sent
 * without the key and fails with a 401, which is the safe direction to be
 * wrong; add it when `specs/auth-ng.json` gives an operation `accessKey`.
 */
const ACCESS_KEY_PATHS: readonly string[] = [
  "/auth/exchange",
  "/auth/exchange-via-email",
  "/auth/get-app-config/",
  "/auth/reset-pwd",
  "/auth/send-link",
  "/auth/send-reset",
  "/auth/user",
  "/auth/user-refresh",
  "/auth/user-via-email",
  "/authz/get-policy",
  "/authz/has-permission",
  "/authz/permission-checks",
  "/authz/validate-token",
  "/org/get-for-user",
  "/org/get-metadata/",
  "/org/search",
];

const needsAccessKey = (path: string) =>
  ACCESS_KEY_PATHS.some((p) =>
    p.endsWith("/") ? path.startsWith(p) : path === p,
  );

const fetchClient = () => {
  const token = LocalStorage.getGlobalJwt();
  const baseUrl = getEnv().authServiceUrl;
  const basePath = new URL(baseUrl).pathname.replace(/\/$/, "");

  const client = createFetchClient<AuthNgSchemaTypes.paths>({
    baseUrl,
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  client.use({
    onRequest: ({ request }) => {
      const parsedUrl = new URL(request.url);
      logger.info(`Executing auth request to '${parsedUrl.pathname}'`);
      const path = parsedUrl.pathname.slice(basePath.length);
      if (needsAccessKey(path)) {
        request.headers.set("X-ACCESS-KEY", getEnv().authAccessKey);
      }
      return request;
    },
  } as Middleware);

  return client;
};

export const apiClient = () => createClient(fetchClient());

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

/**
 * AuthService implementation using the AAA openapi-fetch client.
 * Passed to AuthContextProvider so the state machine can make API calls.
 */
export function createAuthService(): AuthService {
  const client = () => fetchClient();

  return {
    async redirect({ token, tokenType }) {
      const { data, error } = await client().GET("/auth/redirect", {
        params: { query: { token, stytch_token_type: tokenType } },
      });
      if (error) throw error;
      return data!;
    },

    async getOrgsForUser({ email }) {
      const { data, error } = await client().GET("/org/get-for-user", {
        params: { query: { email_addr: email } },
      });
      if (error) throw error;
      return data!;
    },

    async authenticateUser({ email, orgId, password }) {
      const { data, error } = await client().POST("/auth/user", {
        body: { email_addr: email, org_id: orgId, user_pwd: password },
      });
      if (error) throw error;
      return data!;
    },

    async exchange({ orgId, session }) {
      const { data, error } = await client().POST("/auth/exchange", {
        body: { org_id: orgId, session },
      });
      if (error) throw error;
      return data!;
    },

    /**
     * Uses a dedicated fetch client instead of the shared `client()` because
     * this endpoint requires the user JWT - not the AAA token that `client()`
     * reads from localStorage. The AAA token doesn't exist yet; it's the
     * value this call returns.
     */
    async createSession({ locationId, token }) {
      const userJwtClient = () =>
        createFetchClient<AuthNgSchemaTypes.paths>({
          baseUrl: getEnv().authServiceUrl,
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

      // No warehouse yet - the user is signed in but has not entered a product
      // that needs one.
      //
      // /authz/create-session cannot serve this: its schema marks warehouseId
      // required and the handler rejects an unknown one with 401.
      // /authz/exchange-token takes the same user JWT, treats warehouseId as
      // optional, and omits the warehouse claims when it is absent.
      //
      // Both return the signed AAA JWT, under different names - create-session
      // puts it in `tenantId`, exchange-token in `token`.
      if (locationId === null) {
        const { data, error } = await userJwtClient().POST(
          "/authz/exchange-token",
          { body: {} },
        );
        if (error) throw error;
        return { tenantId: data!.token, dakota_session: "" };
      }

      const { data, error } = await userJwtClient().POST(
        "/authz/create-session",
        { body: { warehouseId: locationId } },
      );
      if (error) throw error;
      return data!;
    },

    async refreshUser({ token }) {
      const { data, error } = await client().POST("/auth/user-refresh", {
        body: { token },
      });
      if (error) throw error;
      return data!;
    },

    async getPolicy() {
      const { data, error } = await client().GET("/authz/get-policy");
      if (error) throw error;
      return data!;
    },
  };
}
